import { useEffect, useRef, useState } from 'react';
import { Layers, Search, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUiStore } from '@/lib/store/ui';
import { useAiLayersStore } from '@/lib/store/aiLayers';
import { BASEMAPS } from '@/lib/map/basemaps';
import { AILayerSearchModal } from './AILayerSearchModal';
import { cn } from '@/lib/utils';

interface OverlayToggle {
  key: 'airspace' | 'airports' | 'population';
  label: string;
  icon: string;
}

const OVERLAYS: OverlayToggle[] = [
  { key: 'airspace', label: 'Airspace', icon: '✈' },
  { key: 'airports', label: 'Airports', icon: '🛫' },
  { key: 'population', label: 'Population', icon: '👥' },
];

const WX_LAYERS: Array<{ key: 'rain' | 'wind' | 'radar'; label: string; icon: string }> = [
  { key: 'rain', label: 'Rain', icon: '🌧' },
  { key: 'wind', label: 'Wind', icon: '💨' },
  { key: 'radar', label: 'Radar', icon: '📡' },
];

export function LayerPanel() {
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const overlayAirspace = useUiStore((s) => s.showOverlayAirspace);
  const overlayAirports = useUiStore((s) => s.showOverlayAirports);
  const overlayPopulation = useUiStore((s) => s.showOverlayPopulation);
  const overlays = { airspace: overlayAirspace, airports: overlayAirports, population: overlayPopulation };
  const toggleOverlay = useUiStore((s) => s.toggle);
  const wxOverlay = useUiStore((s) => s.wxOverlay);
  const setWxOverlay = useUiStore((s) => s.setWxOverlay);
  const basemap = useUiStore((s) => s.basemap);
  const setBasemap = useUiStore((s) => s.setBasemap);
  const aiLayers = useAiLayersStore((s) => s.layers);
  const toggleAi = useAiLayersStore((s) => s.toggle);
  const removeAi = useAiLayersStore((s) => s.remove);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    setTimeout(() => window.addEventListener('mousedown', handler));
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="absolute bottom-[calc(0.75rem+var(--map-inset,0px))] left-3 z-10">
      <Button
        variant="outline"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label="Map layers"
        className="bg-(--color-surface)"
      >
        <Layers size={14} />
      </Button>
      {open && (
        <div className="absolute bottom-10 left-0 w-64 rounded-md border border-(--color-border) bg-(--color-surface) shadow-(--shadow-popover) p-2 space-y-2 max-h-[calc(100dvh-var(--nav-h)-8rem)] overflow-y-auto">
          <Heading>Overlays</Heading>
          {OVERLAYS.map((o) => {
            const storeKey =
              o.key === 'airspace' ? 'showOverlayAirspace'
              : o.key === 'airports' ? 'showOverlayAirports'
              : 'showOverlayPopulation';
            return (
              <Row key={o.key} label={`${o.icon} ${o.label}`} on={overlays[o.key]} onToggle={() => toggleOverlay(storeKey)} />
            );
          })}
          <div className="h-px bg-(--color-border-lite)" />
          <Heading>Weather</Heading>
          <div className="text-[0.615em] text-(--color-text3) -mt-1">Select one · GoC MSC GeoMet</div>
          {WX_LAYERS.map((w) => (
            <Row
              key={w.key}
              label={`${w.icon} ${w.label}`}
              on={wxOverlay === w.key}
              onToggle={() => setWxOverlay(w.key)}
            />
          ))}
          <div className="h-px bg-(--color-border-lite)" />
          <div className="flex items-center justify-between">
            <Heading>AI Layers</Heading>
            <Button size="xs" onClick={() => setAiOpen(true)}>
              <Search size={10} /> Search
            </Button>
          </div>
          {aiLayers.length === 0 && <div className="text-[0.692em] text-(--color-text3) italic">No layers added yet</div>}
          {aiLayers.map((l) => (
            <div key={l.id} className="flex items-center gap-1.5 text-[0.769em]">
              <span className="size-2 rounded-full shrink-0" style={{ background: l.color }} />
              <span className={cn('flex-1 truncate', !l.visible && 'opacity-50 line-through')}>{l.name}</span>
              <button onClick={() => toggleAi(l.id)} aria-label="Toggle layer" className="p-1 hover:bg-(--color-bg) rounded">
                {l.visible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button onClick={() => removeAi(l.id)} aria-label="Remove layer" className="p-1 hover:bg-(--color-red-bg) rounded text-(--color-red)">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <div className="h-px bg-(--color-border-lite)" />
          <Heading>Base Map</Heading>
          {BASEMAPS.map((b) => (
            <Row
              key={b.id}
              label={b.label}
              on={basemap === b.url}
              onToggle={() => setBasemap(b.url)}
            />
          ))}
        </div>
      )}
      <AILayerSearchModal open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">{children}</div>;
}

function Row({ label, on, onToggle }: { label: string; on: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-center justify-between text-[0.769em] cursor-pointer" onClick={onToggle}>
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className={cn(
          'w-8 h-4 rounded-full transition-colors relative shrink-0',
          on ? 'bg-(--color-green)' : 'bg-(--color-border)',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-3 rounded-full bg-white transition-all',
            on ? 'left-[18px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  );
}
